import audioTranslationData from "./audioTranslation.json";
import audioTranslationLandingData from "./audioTranslationLanding.json";
import audioTranslationPairSeoData from "./audioTranslationPairSeo.json";
import dubbingLandingData from "./dubbingLanding.json";
import { en } from "../locales/en";
import { translations, type SupportedLanguage } from "../locales";

export type AudioTranslationContentSection = {
  title: string;
  description: string;
};

export type AudioTranslationLandingContent = {
  slug: string;
  title: string;
  description: string;
  seoTitle: string;
  seoDescription: string;
  footerLabel: string;
  breadcrumbHomeLabel: string;
  breadcrumbCurrentLabel: string;
  pairsSectionTitle: string;
  pairsSectionDescription: string;
  contentSections: AudioTranslationContentSection[];
};

export type AudioTranslationPair = {
  sourceCode: string;
  sourceLabel: string;
  sourceSlug: string;
  targetCode: string;
  targetLabel: string;
  targetSlug: string;
  buttonLabel: string;
  pathSlug: string;
};

type AudioTranslationStaticConfig = {
  landing: {
    slug: string;
  };
  pairs: AudioTranslationPair[];
};

type FaqItem = {
  question: string;
  answer: string;
};

type AudioTranslationPairLandingSeo = {
  heroTitle: string;
  heroDescription: string;
  title: string;
  description: string;
  keywords: string;
  faq: FaqItem[];
};

const audioTranslationContent = audioTranslationData as AudioTranslationStaticConfig;
const audioTranslationLandingContentByLanguage = audioTranslationLandingData as Record<
  SupportedLanguage,
  AudioTranslationLandingContent
>;
const dubbingLandingContent = dubbingLandingData as Record<
  SupportedLanguage,
  {
    description: string;
    keywords: string;
    faq: FaqItem[];
  }
>;
const audioTranslationPairSeoTemplates = audioTranslationPairSeoData as Record<
  SupportedLanguage,
  {
    heroTitle: string;
    seoTitle: string;
    seoDescription: string;
  }
>;

const DISPLAY_NAME_LOCALE_BY_LANGUAGE: Partial<Record<SupportedLanguage, string>> = {
  zh: "zh-Hans",
};

const AUDIO_TRANSLATION_PAIR_DISPLAY_PHRASE_TEMPLATES: Record<SupportedLanguage, string> = {
  en: "{source} to {target}",
  ru: "{source} на {target}",
  es: "{source} a {target}",
  fr: "{source} vers {target}",
  de: "{source} auf {target}",
  it: "{source} in {target}",
  pt: "{source} para {target}",
  pl: "{source} na {target}",
  zh: "{source}到{target}",
  ja: "{source}から{target}へ",
  ko: "{source}에서 {target}로",
  ar: "من {source} إلى {target}",
  bg: "{source} на {target}",
  hr: "{source} na {target}",
  cs: "{source} do {target}",
  da: "{source} til {target}",
  nl: "{source} naar {target}",
  et: "{source} keelest {target} keelde",
  fi: "{source} kielestä {target} kieleen",
  el: "{source} σε {target}",
  hu: "{source} nyelvről {target} nyelvre",
  ga: "{source} go {target}",
  lv: "{source} uz {target}",
  lt: "{source} į {target}",
  mt: "{source} għal {target}",
  ro: "{source} în {target}",
  sk: "{source} do {target}",
  sl: "{source} v {target}",
  sv: "{source} till {target}",
};

const AUDIO_TRANSLATION_PAIR_INLINE_PHRASE_TEMPLATES: Record<SupportedLanguage, string> = {
  en: "{source} to {target}",
  ru: "с {source} на {target}",
  es: "de {source} a {target}",
  fr: "de {source} vers {target}",
  de: "von {source} auf {target}",
  it: "dallo {source} verso {target}",
  pt: "do {source} para {target}",
  pl: "z {source} na {target}",
  zh: "{source}到{target}",
  ja: "{source}から{target}",
  ko: "{source}를 {target}로",
  ar: "من {source} إلى {target}",
  bg: "от {source} на {target}",
  hr: "sa {source} na {target}",
  cs: "ze {source} do {target}",
  da: "fra {source} til {target}",
  nl: "van {source} naar {target}",
  et: "{source} keelest {target} keelde",
  fi: "{source} kielestä {target} kieleen",
  el: "από {source} σε {target}",
  hu: "a {source} nyelvről {target} nyelvre",
  ga: "ó {source} go {target}",
  lv: "no {source} uz {target}",
  lt: "iš {source} į {target}",
  mt: "mill-{source} għal {target}",
  ro: "din {source} în {target}",
  sk: "zo {source} do {target}",
  sl: "iz {source} v {target}",
  sv: "från {source} till {target}",
};

const AUDIO_TRANSLATION_PAIR_LOWERCASE_SOURCE_LANGUAGES = new Set<SupportedLanguage>([
  "ru",
  "es",
  "fr",
  "it",
  "pt",
  "pl",
  "bg",
  "hr",
  "cs",
  "da",
  "nl",
  "et",
  "fi",
  "el",
  "hu",
  "ga",
  "lv",
  "lt",
  "mt",
  "ro",
  "sk",
  "sl",
  "sv",
]);

const AUDIO_TRANSLATION_PAIR_LOWERCASE_TARGET_LANGUAGES = new Set<SupportedLanguage>([
  "ru",
  "es",
  "fr",
  "it",
  "pt",
  "pl",
  "bg",
  "hr",
  "cs",
  "da",
  "nl",
  "et",
  "fi",
  "el",
  "hu",
  "ga",
  "lv",
  "lt",
  "mt",
  "ro",
  "sk",
  "sl",
  "sv",
]);

const displayNamesCache = new Map<string, Intl.DisplayNames | null>();

export const AUDIO_TRANSLATION_LANDING_PATH = `/${audioTranslationLandingContentByLanguage.en.slug}` as const;

export const AUDIO_TRANSLATION_LANDING_PATHS_BY_LANGUAGE = Object.fromEntries(
  Object.entries(audioTranslationLandingContentByLanguage).map(([language, entry]) => [
    language,
    language === "en" ? `/${entry.slug}` : `/${language}/${entry.slug}`,
  ]),
) as Record<SupportedLanguage, string>;

function normalizePathname(pathname: string): string {
  if (!pathname) return "/";
  if (pathname === "/") return "/";
  return pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
}

export function getAudioTranslationLandingContent(
  language: SupportedLanguage
): AudioTranslationLandingContent {
  return (
    audioTranslationLandingContentByLanguage[language] ||
    audioTranslationLandingContentByLanguage.en
  );
}

export function getAudioTranslationFooterLabel(language: SupportedLanguage): string {
  return getAudioTranslationLandingContent(language).footerLabel;
}

export function getAudioTranslationLandingPath(language: SupportedLanguage): string {
  return (
    AUDIO_TRANSLATION_LANDING_PATHS_BY_LANGUAGE[language] ||
    AUDIO_TRANSLATION_LANDING_PATHS_BY_LANGUAGE.en
  );
}

export function getAudioTranslationPairs(): AudioTranslationPair[] {
  return audioTranslationContent.pairs;
}

export function buildAudioTranslationPairPath(pairOrPathSlug: AudioTranslationPair | string): string {
  const pathSlug = typeof pairOrPathSlug === "string" ? pairOrPathSlug : pairOrPathSlug.pathSlug;
  return `${AUDIO_TRANSLATION_LANDING_PATH}/${pathSlug}`;
}

export function getAudioTranslationPairByPath(pathname: string): AudioTranslationPair | null {
  const normalizedPathname = normalizePathname(pathname);
  return (
    getAudioTranslationPairs().find(
      (pair) => normalizePathname(buildAudioTranslationPairPath(pair)) === normalizedPathname
    ) || null
  );
}

export function isAudioTranslationPairPath(pathname: string): boolean {
  return Boolean(getAudioTranslationPairByPath(pathname));
}

function getDisplayNames(locale: string): Intl.DisplayNames | null {
  if (!("DisplayNames" in Intl)) {
    return null;
  }

  if (!displayNamesCache.has(locale)) {
    try {
      displayNamesCache.set(locale, new Intl.DisplayNames([locale], { type: "language" }));
    } catch {
      displayNamesCache.set(locale, null);
    }
  }

  return displayNamesCache.get(locale) || null;
}

function capitalizeLanguageDisplayName(label: string, locale: string): string {
  if (!label) {
    return label;
  }

  return label.charAt(0).toLocaleUpperCase(locale) + label.slice(1);
}

function getLocalizedLanguageDisplayName(
  language: SupportedLanguage,
  languageCode: string,
  fallbackLabel: string
): string {
  const projectTranslationLabel = translations[language]?.languageOptions?.[
    languageCode as keyof typeof en.languageOptions
  ];

  if (
    projectTranslationLabel &&
    (language === "en" || projectTranslationLabel !== en.languageOptions[languageCode as keyof typeof en.languageOptions])
  ) {
    return projectTranslationLabel;
  }

  const locale = DISPLAY_NAME_LOCALE_BY_LANGUAGE[language] || language;
  const displayNames = getDisplayNames(locale);
  const localizedLabel = displayNames?.of(languageCode);

  return capitalizeLanguageDisplayName(localizedLabel || fallbackLabel, locale);
}

function applyAudioTranslationPairSeoTemplate(
  template: string,
  sourceLabel: string,
  targetLabel: string,
  sourceLowerLabel: string,
  targetLowerLabel: string,
  pairLabel: string,
  pairInlineLabel: string
): string {
  return template
    .replaceAll("{source}", sourceLabel)
    .replaceAll("{target}", targetLabel)
    .replaceAll("{sourceLower}", sourceLowerLabel)
    .replaceAll("{targetLower}", targetLowerLabel)
    .replaceAll("{pair}", pairLabel)
    .replaceAll("{pairLower}", pairInlineLabel);
}

function getAudioTranslationPairPhrase(
  language: SupportedLanguage,
  pair: AudioTranslationPair,
  variant: "display" | "inline"
): string {
  const locale = DISPLAY_NAME_LOCALE_BY_LANGUAGE[language] || language;
  const localizedSourceLabel = getLocalizedLanguageDisplayName(
    language,
    pair.sourceCode,
    pair.sourceLabel
  );
  const localizedTargetLabel = getLocalizedLanguageDisplayName(
    language,
    pair.targetCode,
    pair.targetLabel
  );
  const sourceLabel =
    variant === "inline" && AUDIO_TRANSLATION_PAIR_LOWERCASE_SOURCE_LANGUAGES.has(language)
      ? localizedSourceLabel.toLocaleLowerCase(locale)
      : localizedSourceLabel;
  const targetLabel = AUDIO_TRANSLATION_PAIR_LOWERCASE_TARGET_LANGUAGES.has(language)
    ? localizedTargetLabel.toLocaleLowerCase(locale)
    : localizedTargetLabel;
  const template =
    variant === "inline"
      ? AUDIO_TRANSLATION_PAIR_INLINE_PHRASE_TEMPLATES[language] ||
        AUDIO_TRANSLATION_PAIR_INLINE_PHRASE_TEMPLATES.en
      : AUDIO_TRANSLATION_PAIR_DISPLAY_PHRASE_TEMPLATES[language] ||
        AUDIO_TRANSLATION_PAIR_DISPLAY_PHRASE_TEMPLATES.en;

  return template
    .replaceAll("{source}", sourceLabel)
    .replaceAll("{target}", targetLabel);
}

export function getAudioTranslationPairButtonLabel(
  language: SupportedLanguage,
  pair: AudioTranslationPair
): string {
  return getAudioTranslationPairPhrase(language, pair, "display");
}

export function getAudioTranslationPairLandingSeo(
  language: SupportedLanguage,
  pair: AudioTranslationPair
): AudioTranslationPairLandingSeo {
  const baseSeo = dubbingLandingContent[language] || dubbingLandingContent.en;
  const seoTemplate = audioTranslationPairSeoTemplates[language] || audioTranslationPairSeoTemplates.en;
  const localizedSourceLabel = getLocalizedLanguageDisplayName(
    language,
    pair.sourceCode,
    pair.sourceLabel
  );
  const localizedTargetLabel = getLocalizedLanguageDisplayName(
    language,
    pair.targetCode,
    pair.targetLabel
  );
  const locale = DISPLAY_NAME_LOCALE_BY_LANGUAGE[language] || language;
  const localizedSourceLowerLabel = localizedSourceLabel.toLocaleLowerCase(locale);
  const localizedTargetLowerLabel = localizedTargetLabel.toLocaleLowerCase(locale);
  const pairLabel = getAudioTranslationPairPhrase(language, pair, "display");
  const pairInlineLabel = getAudioTranslationPairPhrase(language, pair, "inline");
  const heroTitle = applyAudioTranslationPairSeoTemplate(
    seoTemplate.heroTitle,
    localizedSourceLabel,
    localizedTargetLabel,
    localizedSourceLowerLabel,
    localizedTargetLowerLabel,
    pairLabel,
    pairInlineLabel
  );
  const heroDescription = baseSeo.description;

  return {
    heroTitle,
    heroDescription,
    title: applyAudioTranslationPairSeoTemplate(
      seoTemplate.seoTitle,
      localizedSourceLabel,
      localizedTargetLabel,
      localizedSourceLowerLabel,
      localizedTargetLowerLabel,
      pairLabel,
      pairInlineLabel
    ),
    description: applyAudioTranslationPairSeoTemplate(
      seoTemplate.seoDescription,
      localizedSourceLabel,
      localizedTargetLabel,
      localizedSourceLowerLabel,
      localizedTargetLowerLabel,
      pairLabel,
      pairInlineLabel
    ),
    keywords: `${baseSeo.keywords}, ${pair.sourceSlug} to ${pair.targetSlug} voice over, ${pair.sourceSlug} audio translation, ${pair.sourceSlug} to ${pair.targetSlug} audio translation`,
    faq: baseSeo.faq,
  };
}
